import { z } from "zod";
import { esiCredentialStateSchema } from "./account.ts";
import { directorySourceKindSchema } from "./directory.ts";
import { entityIdSchema, roleSchema, userIdSchema, uuidSchema } from "./tenancy.ts";

// ── Users ────────────────────────────────────────────────────────────────────────────────

/** `GET /api/admin/users`: search by name or e-mail, paged by e-mail (`next` of the last page). */
export const adminUserQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  after: z.string().max(320).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type AdminUserQuery = z.infer<typeof adminUserQuerySchema>;

/** A directory source that knows the user (`active: false` once it deactivated them). */
export const userSourceSchema = z.object({
  id: uuidSchema,
  kind: directorySourceKindSchema,
  name: z.string(),
  active: z.boolean(),
});
export type UserSource = z.infer<typeof userSourceSchema>;

/**
 * A user as the backoffice lists them. `memberships` holds the ones in the caller's scope only;
 * `outsideScope` counts the others, which also stop an entity admin from changing the user.
 */
export const adminUserSchema = z.object({
  id: userIdSchema,
  email: z.string(),
  displayName: z.string().nullable(),
  status: z.enum(["active", "disabled"]),
  lastSignInAt: z.iso.datetime().nullable(),
  memberships: z.array(
    z.object({ entityId: entityIdSchema, entityName: z.string(), roles: z.array(roleSchema) }),
  ),
  outsideScope: z.int().min(0),
  sources: z.array(userSourceSchema),
  /** Whether the caller may disable or enable this user. */
  manageable: z.boolean(),
  /**
   * Invited and no password set yet: the latest link's expiry, past or not. Never a link or
   * token. Optional for one release (an N-1 API omits it).
   */
  invitation: z.object({ expiresAt: z.iso.datetime() }).nullable().optional(),
  /**
   * The person's ESI credential beside their SSO (#419): held, and allowed by their domain now.
   * In a user's detail for tenant admins only; absent from listings and older releases.
   */
  esiCredential: esiCredentialStateSchema.optional(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

/**
 * `POST /api/admin/users/:userId/invitation-link`: a fresh invitation link, returned once. It
 * replaces every earlier link of the user, the emailed one included.
 */
export const invitationLinkSchema = z.object({
  link: z.url(),
  expiresAt: z.iso.datetime(),
});
export type InvitationLink = z.infer<typeof invitationLinkSchema>;

export const adminUserPageSchema = z.object({
  users: z.array(adminUserSchema),
  /** Cursor for the next page, or null on the last one. */
  next: z.string().nullable(),
});
export type AdminUserPage = z.infer<typeof adminUserPageSchema>;

/** `PATCH /api/admin/users/:userId`. */
export const setUserStatusRequestSchema = z.strictObject({
  status: z.enum(["active", "disabled"]),
});
export type SetUserStatusRequest = z.infer<typeof setUserStatusRequestSchema>;

// ── Entities ─────────────────────────────────────────────────────────────────────────────

/** `PATCH /api/admin/entities/:entityId`: rename, move, or both. */
export const updateEntityRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(200).optional(),
    parentId: uuidSchema.optional(),
  })
  .refine((v) => v.name !== undefined || v.parentId !== undefined, {
    message: "Give a new name or a new parent",
  });
export type UpdateEntityRequest = z.infer<typeof updateEntityRequestSchema>;

/**
 * What still holds an entity: deleting it is refused (`409 entity_not_empty`) unless all are 0.
 * `promotions` counts pending promotion requests to it.
 */
export const entityUsageSchema = z.object({
  children: z.int().min(0),
  members: z.int().min(0),
  projects: z.int().min(0),
  groups: z.int().min(0),
  orgCodes: z.int().min(0),
  promotions: z.int().min(0),
});
export type EntityUsage = z.infer<typeof entityUsageSchema>;

// ── Settings catalogue ───────────────────────────────────────────────────────────────────

/** How a setting's value is edited. `json` is for structured values (schemes, guidelines). */
export const settingInputSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("boolean") }),
  z.object({ type: z.literal("select"), options: z.array(z.string()) }),
  z.object({ type: z.literal("multiselect"), options: z.array(z.string()) }),
  z.object({
    type: z.literal("number"),
    min: z.number().nullable(),
    max: z.number().nullable(),
    nullable: z.boolean(),
  }),
  z.object({ type: z.literal("json") }),
  /** `include` and `exclude` lists of domains (`tools.webSearch.domains`), `max` each. */
  z.object({ type: z.literal("domains"), max: z.int().min(1) }),
]);
export type SettingInput = z.infer<typeof settingInputSchema>;

/** Why a setting cannot be changed at this level. */
export const settingLockSchema = z.discriminatedUnion("level", [
  /** The deployment bounds it (`reason` explains, e.g. no global inference). */
  z.object({ level: z.literal("instance"), reason: z.string() }),
  z.object({ level: z.literal("tenant") }),
  z.object({ level: z.literal("entity"), entityId: entityIdSchema, name: z.string() }),
]);
export type SettingLock = z.infer<typeof settingLockSchema>;

export const settingsCatalogueEntrySchema = z.object({
  key: z.string(),
  /** The key's prefix (`tools`, `models`, `knowledge`, …), to group the screen. */
  area: z.string(),
  input: settingInputSchema,
  /** The resolved value at this level, and where it comes from. */
  value: z.unknown(),
  source: z.enum(["instance", "tenant", "entity"]),
  sourceEntityId: entityIdSchema.nullable(),
  /** The source entity's name, also when it is above the caller's scope. */
  sourceName: z.string().nullable(),
  /** This level holds its own value (a reset returns to the inherited one). */
  ownValue: z.boolean(),
  /**
   * With `ownValue`: the value a reset brings back, so the row asks first when it loosens
   * (#365). Absent from an N-1 API.
   */
  inherited: z.object({ value: z.unknown() }).optional(),
  /** This level's own value locks the levels below. */
  lockedHere: z.boolean(),
  /** Set when a level above (or the instance) fixes the value: read-only here. */
  lockedBy: settingLockSchema.nullable(),
  /** Why the key has no effect on this instance (e.g. its tool is not deployed), if so. */
  unavailable: z.string().optional(),
});
export type SettingsCatalogueEntry = z.infer<typeof settingsCatalogueEntrySchema>;

export const settingsCatalogueSchema = z.object({
  level: z.object({ entityId: entityIdSchema.nullable(), name: z.string() }),
  settings: z.array(settingsCatalogueEntrySchema),
});
export type SettingsCatalogue = z.infer<typeof settingsCatalogueSchema>;

export const settingsCatalogueQuerySchema = z.object({ entity: uuidSchema.optional() });
